import { useState, useRef, useContext } from "react";
import { Crop, PixelCrop, centerCrop, makeAspectCrop } from "react-image-crop";
import { EnvContext } from "@/contexts/appContexts";
import { saveImageToFirebase } from "@/utils/firebase";
import { toast } from "sonner";

export const useProfilePhotoUpload = (userId?: number, onPhotoUploaded?: (url: string) => void) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [imgSrc, setImgSrc] = useState("");
  const [imgRef, setImgRef] = useState<HTMLImageElement | null>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [isCropping, setIsCropping] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const envValues = useContext(EnvContext);

  const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const reader = new FileReader();
      reader.addEventListener("load", () => setImgSrc(reader.result?.toString() || ""));
      reader.readAsDataURL(e.target.files[0]);
      setIsCropping(true);
    }
  };

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    setImgRef(e.currentTarget);

    const initialCrop = centerCrop(
      makeAspectCrop(
        {
          unit: "%",
          width: 80,
        },
        1,
        width,
        height
      ),
      width,
      height
    );

    setCrop(initialCrop);
  };

  const getCroppedImg = (image: HTMLImageElement, crop: PixelCrop): Promise<Blob> => {
    const canvas = document.createElement("canvas");
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = crop.width * scaleX;
    canvas.height = crop.height * scaleY;
    const ctx = canvas.getContext("2d");

    if (ctx) {
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";

      ctx.drawImage(
        image,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        0,
        0,
        canvas.width,
        canvas.height
      );
    }

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        blob => {
          if (!blob) {
            reject(new Error("Canvas is empty"));
            return;
          }
          resolve(blob);
        },
        "image/jpeg",
        0.95
      );
    });
  };

  const handleSaveCrop = async () => {
    if (!userId) {
      toast.error("User ID is missing");
      return;
    }
    if (imgRef && completedCrop) {
      setLoading(true);
      try {
        const blob = await getCroppedImg(imgRef, completedCrop);
        const fileName = `profile_${Date.now()}`;
        const uploadedUrl = await saveImageToFirebase(envValues, userId, blob, fileName);
        if (onPhotoUploaded) {
          onPhotoUploaded(uploadedUrl);
        }
        setIsCropping(false);
        toast.success("Photo uploaded successfully");
      } catch (e) {
        console.error(e);
        toast.error("Failed to upload photo");
      } finally {
        setLoading(false);
      }
    }
  };

  return {
    loading,
    imgSrc,
    crop,
    completedCrop,
    isCropping,
    fileInputRef,
    setCrop,
    setCompletedCrop,
    setIsCropping,
    onSelectFile,
    onImageLoad,
    handleSaveCrop,
  };
};
